import React, {
	useCallback,
	useEffect,
	useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../stores/types/types';
import useShots from '../../contexts/shots/provider';
import { AUTOPLAY_SECOND_BUFFER } from '../../hooks/use-shot-utilities';
import { UpdateScheduledStreamStatus } from '../../connection/scheduled-streams';
import { ScheduledStreamStatus } from '../../stores/types/connection';

export default function EmbedController() {
	const user = useSelector((state: GS) => state.SessionReducer.user);
	const embed = useSelector((state: GS) => state.SessionReducer.embed);
	const token = useSelector((state: GS) => state.SessionReducer.token);
	const meeting = useSelector((state: GS) => state.SessionReducer.meeting);
	const shots = useSelector((state: GS) => state.ShotsReducer.shots);
	const scheduledStream = useSelector((s: GS) => s.MeetingReducer.scheduledStream);

	const { currentShot, lockAutoPlay } = useShots();

	const {
		shot_id: shotId,
		shot_data: {
			autoplay: autoPlay,
		},
	} = currentShot;

	const [showEnded, setShowEnded] = React.useState<boolean>(false);
	const lastShotTimmy = React.useRef<NodeJS.Timeout | null>(null);

	const updateScheduledStreamStatus = useCallback((status: ScheduledStreamStatus) => {
		if (
			!token
			|| !meeting?.meeting
			|| !scheduledStream
		) {
			return;
		}

		if (scheduledStream.id && scheduledStream.startTime) {
			UpdateScheduledStreamStatus(
				token,
				meeting.meeting,
				scheduledStream.id,
				1,
				status,
			);
		}
	}, [scheduledStream, meeting, user, embed]);

	const isLastShot = useMemo(() => {
		const currShotIsLastShot = shotId === shots[shots.length - 1].shot_id;
		if (!currShotIsLastShot && showEnded) {
			setShowEnded(false);
			updateScheduledStreamStatus(ScheduledStreamStatus.None);
		}
		return currShotIsLastShot;
	}, [shotId, shots, showEnded, updateScheduledStreamStatus]);

	useEffect(() => {
		const endScheduledStream = isLastShot && lockAutoPlay && autoPlay;
		if (endScheduledStream && !showEnded) {
			// after autoplay is locked, the time remaining in the shot is the buffer
			setShowEnded(true);
			lastShotTimmy.current = setTimeout(() => {
				updateScheduledStreamStatus(ScheduledStreamStatus.Ended);
			}, 1000 * AUTOPLAY_SECOND_BUFFER + 200);
		} else if (!endScheduledStream && showEnded) {
			setShowEnded(false);
			if (lastShotTimmy.current) clearTimeout(lastShotTimmy.current);
		}
	}, [
		shotId,
		autoPlay,
		shots,
		lockAutoPlay,
		isLastShot,
		showEnded,
		updateScheduledStreamStatus,
	]);

	return (<></>);
}
